import { pdf } from '@react-pdf/renderer';
import { PDFComponent } from './PDFComponent';

/**
 * Convierte un DTO de cotización en un File PDF real, con el MISMO componente.
 *
 * ── Por qué existe ───────────────────────────────────────────────────────
 * El PDF oficial lo genera `PDFComponent` en el navegador. Esa generación estaba
 * enterrada dentro de `useBudgetPDF`, que además pide los datos al servidor, exige
 * un objeto `persona` y muestra toasts. Nada de eso sirve en la bandeja de
 * conversaciones, donde los datos llegan de otro endpoint y los errores se pintan
 * en la tarjeta del lead, no en un toast del cotizador.
 *
 * Así que la generación —y sólo la generación— vive aquí.
 *
 * 🔴 Es EL MISMO componente, no una copia. El documento que el asesor manda por
 * WhatsApp tiene que ser idéntico al que el cliente descarga por su enlace y al que
 * va adjunto por correo. Con un render aparte, cualquier cambio de diseño se
 * aplicaría a unos clientes y no a otros, y nadie se daría cuenta hasta que un
 * cliente comparara dos PDFs.
 *
 * @param {{resumen: object, detalle: array, pdfPlazo?: array}} data DTO del backend
 * @param {string} filename nombre con el que WhatsApp lo va a mostrar
 * @returns {Promise<File>} PDF real, no un blob con extensión .pdf
 * @throws {Error} si el DTO no trae con qué renderizar o el renderer falla
 */
export const buildBudgetPdfFile = async (data, filename) => {
  if (!data?.resumen || !data?.detalle) {
    // 🔴 Cortar aquí y no dejar que el renderer siga: `PDFComponent` pinta las
    // celdas vacías sin quejarse, y el asesor mandaría al cliente una cotización
    // en blanco con el logo de la empresa.
    throw new Error('La cotización no trae datos suficientes para generar el PDF.');
  }

  const blob = await pdf(
    <PDFComponent
      resumen={data.resumen}
      detalle={data.detalle}
      allDetalleState={data.pdfPlazo || []}
    />,
  ).toBlob();

  // 🔴 El nombre con .pdf es obligatorio, no cosmético: la ruta temporal que el
  // backend le pasa a la Media API de Meta no tiene extensión, y Meta rechaza el
  // archivo si el filename no la trae.
  const nombre = (filename || '').trim() || 'cotizacion.pdf';

  return new File([blob], nombre.toLowerCase().endsWith('.pdf') ? nombre : `${nombre}.pdf`, {
    type: 'application/pdf',
  });
};
